import { useState, useRef, useEffect } from 'react';
import './CreateEventPanel.css';
import { eventsAPI } from './services/api';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

function CreateEventPanel({ currentUser, allUsers, onEventCreated }) {
  const { t } = useTranslation();
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventHour, setEventHour] = useState(0);
  const [eventMinute, setEventMinute] = useState(0);
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [showFriendDialog, setShowFriendDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);
  const [hashtagInput, setHashtagInput] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Helper function to update location
  const updateLocation = (lat, lng, formattedAddress) => {
    setAddress(formattedAddress);
    setLatitude(lat);
    setLongitude(lng);

    // Update input field
    if (addressInputRef.current) {
      addressInputRef.current.value = formattedAddress;
    }

    // Update marker on map
    if (markerRef.current && mapInstanceRef.current) {
      markerRef.current.setPosition({ lat, lng });
      mapInstanceRef.current.panTo({ lat, lng });
    }
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    // Wait for Google Maps to load
    const initAutocomplete = () => {
      if (!addressInputRef.current) {
        console.log('Address input ref not ready');
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Maps API not loaded yet, retrying...');
        setTimeout(initAutocomplete, 100);
        return;
      }

      try {
        // Clean up existing autocomplete if any
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          { types: ['establishment', 'geocode'] }
        );

        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (place.geometry) {
            const formattedAddress = place.formatted_address || '';
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            updateLocation(lat, lng, formattedAddress);
            console.log('Place selected:', formattedAddress);
          }
        });

        console.log('Google Places Autocomplete initialized successfully');
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };

    initAutocomplete();

    // Cleanup
    return () => {
      if (autocompleteRef.current && window.google && window.google.maps) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current) {
        return;
      }

      if (!window.google || !window.google.maps) {
        setTimeout(initMap, 100);
        return;
      }

      try {
        // Default center (Tel Aviv, Israel - you can change this)
        const defaultCenter = { lat: 32.0853, lng: 34.7818 };
        const center = latitude && longitude ? { lat: latitude, lng: longitude } : defaultCenter;

        // Create map
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: center,
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        // Create marker
        markerRef.current = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position: center,
          draggable: true,
        });

        // Add click listener to map
        mapInstanceRef.current.addListener('click', async (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          // Move marker
          markerRef.current.setPosition({ lat, lng });

          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          try {
            const result = await geocoder.geocode({ location: { lat, lng } });
            if (result.results[0]) {
              updateLocation(lat, lng, result.results[0].formatted_address);
            }
          } catch (error) {
            console.error('Geocoding error:', error);
            updateLocation(lat, lng, `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        });

        // Add drag listener to marker
        markerRef.current.addListener('dragend', async (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          try {
            const result = await geocoder.geocode({ location: { lat, lng } });
            if (result.results[0]) {
              updateLocation(lat, lng, result.results[0].formatted_address);
            }
          } catch (error) {
            console.error('Geocoding error:', error);
            updateLocation(lat, lng, `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        });

        console.log('Google Map initialized successfully');
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initMap();
  }, []);

  // Filter out current user and already selected friends
  const availableFriends = allUsers.filter(user =>
    user.id !== currentUser.id &&
    !selectedFriends.find(f => f.id === user.id) &&
    (user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const addFriend = (friend) => {
    setSelectedFriends([...selectedFriends, friend]);
    setSearchTerm('');
  };

  const removeFriend = (friendId) => {
    setSelectedFriends(selectedFriends.filter(f => f.id !== friendId));
  };

  const addHashtag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = hashtagInput.trim().replace(/^#/, '');
      if (tag && !hashtags.includes(tag)) {
        setHashtags([...hashtags, tag]);
        setHashtagInput('');
      }
    }
  };

  const addHashtagFromButton = () => {
    const tag = hashtagInput.trim().replace(/^#/, '');
    if (tag && !hashtags.includes(tag)) {
      setHashtags([...hashtags, tag]);
      setHashtagInput('');
    }
  };

  const removeHashtag = (tagToRemove) => {
    setHashtags(hashtags.filter(tag => tag !== tagToRemove));
  };

  const handleCreateEvent = async () => {
    // Get address from input field directly in case state hasn't updated
    const currentAddress = addressInputRef.current?.value || address;

    if (!captchaToken) return;

    if (!eventName || !eventDate || !currentAddress) {
      alert(t('createEvent.alerts.fillDetails'));
      return;
    }

    if (selectedFriends.length < 2) {
      alert(t('createEvent.alerts.needMoreFriends'));
      return;
    }

    try {
      setCreating(true);

      // Build time string from hour and minute
      const eventTime = `${String(eventHour).padStart(2, '0')}:${String(eventMinute).padStart(2, '0')}`;

      // Validate that the event is at least 5 minutes in the future
      const eventDateTime = new Date(`${eventDate}T${eventTime}:00`);
      const now = new Date();
      const minFutureTime = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes from now

      if (eventDateTime < minFutureTime) {
        const timeDiff = Math.round((minFutureTime - eventDateTime) / (60 * 1000));
        if (eventDateTime < now) {
          alert(t('createEvent.alerts.pastDate'));
        } else {
          alert(t('createEvent.alerts.tooSoon', { minutes: timeDiff }));
        }
        setCreating(false);
        return;
      }

      const eventData = {
        name: eventName,
        date: eventDate,
        time: eventTime,
        address: currentAddress,
        latitude: latitude,
        longitude: longitude,
        participantIds: selectedFriends.map(f => f.id),
        hashtags: hashtags
      };

      const savedEvent = await eventsAPI.create(eventData);
      console.log('Event created:', savedEvent);

      alert(t('createEvent.alerts.created', { name: eventName }));

      // Reset form
      setEventName('');
      setEventDate('');
      setEventHour(0);
      setEventMinute(0);
      setAddress('');
      setLatitude(null);
      setLongitude(null);
      setSelectedFriends([]);
      setHashtags([]);

      // Reset address input manually
      if (addressInputRef.current) {
        addressInputRef.current.value = '';
      }

      // Close dialog if callback provided
      if (onEventCreated) {
        onEventCreated();
      }

    } catch (error) {
      console.error('Error creating event:', error);
      captchaRef.current?.reset();
      setCaptchaToken(null);
      const errorMsg = error.response?.data || error.message || 'Unknown error';
      alert(t('createEvent.alerts.failed', { error: errorMsg }));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="create-event-panel">
      <h3>{t('createEvent.title')}</h3>

      <div className="event-form">
        <div className="form-group">
          <label>{t('createEvent.description')}</label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder={t('createEvent.descriptionPlaceholder')}
          />
        </div>

        <div className="form-group">
          <label>{t('createEvent.hashtags')}</label>
          <div className="hashtag-input-container">
            <input
              type="text"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={addHashtag}
              placeholder={t('createEvent.hashtagPlaceholder')}
            />
            <button
              type="button"
              className="add-hashtag-btn"
              onClick={addHashtagFromButton}
              title="Add hashtag"
            >
              +
            </button>
          </div>
          <div className="selected-hashtags">
            {hashtags.map(tag => (
              <div key={tag} className="hashtag-chip">
                #{tag}
                <span
                  className="remove-hashtag"
                  onClick={() => removeHashtag(tag)}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
          <p className="helper-text">{t('createEvent.hashtagHelper')}</p>
        </div>

        <div className="form-group">
          <label>{t('createEvent.address')}</label>
          <input
            ref={addressInputRef}
            type="text"
            defaultValue={address}
            placeholder={t('createEvent.addressPlaceholder')}
            onBlur={(e) => setAddress(e.target.value)}
          />
          <p className="helper-text">{t('createEvent.addressHelper')}</p>

          {/* Google Map */}
          <div ref={mapRef} className="location-map"></div>
        </div>

        <div className="form-group">
          <label>{t('createEvent.dateTime')}</label>
          <div className="datetime-row">
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="date-input"
            />
            <div className="time-picker">
              <select
                value={eventHour}
                onChange={(e) => setEventHour(parseInt(e.target.value))}
                className="time-select"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                ))}
              </select>
              <span className="time-separator">:</span>
              <select
                value={eventMinute}
                onChange={(e) => setEventMinute(parseInt(e.target.value))}
                className="time-select"
              >
                {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                  <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="helper-text">{t('createEvent.dateTimeHelper')}</p>
        </div>

        <div className="form-group">
          <label>{t('createEvent.participants', { count: selectedFriends.length + 1 })}</label>
          <button
            className="add-friend-btn"
            onClick={() => setShowFriendDialog(true)}
          >
            {t('createEvent.addFriends')}
          </button>

          <div className="selected-friends">
            <div className="friend-chip organizer-chip">
              {t('createEvent.youOrganizer', { name: currentUser.displayName })}
            </div>
            {selectedFriends.map(friend => (
              <div key={friend.id} className="friend-chip">
                {friend.displayName}
                <span
                  className="remove-friend"
                  onClick={() => removeFriend(friend.id)}
                >
                  ×
                </span>
              </div>
            ))}
          </div>

          {selectedFriends.length < 2 && (
            <p className="helper-text">{t('createEvent.addMorePeople')}</p>
          )}
        </div>

        <ReCAPTCHA
          ref={captchaRef}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={(token) => setCaptchaToken(token)}
          onExpired={() => setCaptchaToken(null)}
        />

        <button
          className="create-event-btn"
          onClick={handleCreateEvent}
          disabled={creating || !captchaToken}
        >
          {creating ? t('createEvent.creating') : t('createEvent.create')}
        </button>
      </div>

      {showFriendDialog && (
        <div className="friend-dialog-overlay" onClick={() => setShowFriendDialog(false)}>
          <div className="friend-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h4>{t('createEvent.friendDialog.title')}</h4>
              <button
                className="close-dialog"
                onClick={() => setShowFriendDialog(false)}
              >
                ×
              </button>
            </div>

            <div className="search-box">
              <input
                type="text"
                placeholder={t('createEvent.friendDialog.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            <div className="friend-list">
              {availableFriends.length === 0 ? (
                <p className="no-results">{t('createEvent.friendDialog.noFriends')}</p>
              ) : (
                availableFriends.map(friend => (
                  <div key={friend.id} className="friend-item">
                    <div className="friend-info">
                      <strong>{friend.displayName}</strong>
                      <span className="friend-username">@{friend.username}</span>
                      <span className="friend-gender">{friend.gender}</span>
                    </div>
                    <button
                      className="add-btn"
                      onClick={() => {
                        addFriend(friend);
                        if (availableFriends.length === 1) {
                          setShowFriendDialog(false);
                        }
                      }}
                    >
                      {t('createEvent.friendDialog.add')}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEventPanel;
